/**
 * The host's side of a lobby: who is seated, who is ready, and what everyone else is told.
 *
 * One machine decides, so this is where every fact about a session's membership is made. A guest
 * sends three things and is told everything else. The roster that goes out is whole state rather
 * than a change list, because a guest is connected only to the host and has no other way of hearing
 * that a third player exists.
 *
 * A guest that drops has its seat held rather than freed, and the hold is what makes a reconnect
 * the same player instead of a new one. A held seat still counts against the four, so nobody takes
 * the chair somebody is walking back to. The body leaves the world at once either way: a quarter of
 * a minute of a frozen figure standing in a firing lane where it can be shot is worse than a clean
 * removal and a fresh body when they get back.
 *
 * Everything that reads a clock, sets a timer or draws a random number arrives as an argument, so
 * the whole thing runs in a test without a browser.
 */

import {
  announceSheet,
  claim,
  createSeatTable,
  defaultSeatName,
  expire,
  hold,
  occupancy,
  DEFAULT_SEAT_SHEET,
  reclaim,
  release,
  rename,
  seatOfPeer,
  seatOfPlayer,
  setReady,
} from '@/net/session/seatTable';
import { createSeatDirectory } from '@/net/session/seatDirectory';
import { decodeLobby, encodeLobby } from '@/net/wire/lobbyFrame';
import { joinedNotice, leftNotice, rejoinedNotice } from '@/net/lobby/lobbyNotices';
import { MESSAGE_KINDS } from '@/net/wire/messageKinds';
import { createRttWindow } from '@/net/lobby/rttWindow';
import { HEARTBEAT_EVERY_MS } from '@/net/session/peerHeartbeat';
import { qualityEqual, qualityOf } from '@/net/lobby/lobbyQuality';
import { SEAT_HOLD_MS } from '@/net/session/sessionLimits';
import { silentListeners } from '@/net/transportContract';
import type { CharacterSheet } from '@/sim/progression/characterSheet';
import type { LeftReason, LobbyMessage, RosterSeat } from '@/net/wire/lobbyFrame';
import type { LinkQuality, QualitySignals } from '@/net/lobby/lobbyQuality';
import type { LobbyEvent } from '@/net/lobby/lobbyState';
import type { PeerId, Transport, TransportListeners } from '@/net/transportContract';
import type { Seat, SeatTable } from '@/net/session/seatTable';
import type { RttWindow } from '@/net/lobby/rttWindow';
import type { SeatDirectory } from '@/net/session/seatDirectory';
import type { SessionNotice } from '@/net/lobby/lobbyNotices';
import { DEFAULT_SESSION_RULES, rulesEqual } from '@/net/session/sessionRules';
import type { SessionRules } from '@/net/session/sessionRules';

/** How often a hold is checked for having run out. A second's slack on fifteen is invisible. */
const EXPIRY_TICK_MS = 1000;

export interface LobbyHostOptions {
  readonly transport: Transport;
  /** Local milliseconds. Nothing here goes on the wire, so no clock has to agree with any other. */
  readonly nowMs: () => number;
  /** Returns its own cancel function, in the same shape as everything else that unsubscribes. */
  readonly repeat: (everyMs: number, run: () => void) => () => void;
  readonly onEvent: (event: LobbyEvent) => void;
  readonly onNotice: (notice: SessionNotice) => void;
  /** Eight random bytes as hex. Injected so the seat table itself stays pure. */
  readonly mintToken: () => string;
  /** What this machine calls itself, for its own seat. Falls back to the player number. */
  readonly playerName?: string | undefined;
  /** Who this machine is playing as, for its own seat. Absent leaves the seat on the baseline. */
  readonly playerSheet?: CharacterSheet | undefined;
}

export interface LobbyHost {
  /** Handed to `relayTo`, so this sees the same events as everything else on the transport. */
  readonly listeners: TransportListeners;
  readonly seats: SeatDirectory;
  /**
   * The tokens of the seats kept for somebody absent. The handshake counts them against the four,
   * and checks a returning guest's claim against them so that count does not shut its own player
   * out of the chair being kept for them.
   */
  heldTokens(): readonly string[];
  /** Sets how the room is played and tells everyone. Host only, which is the whole point. */
  setRules(rules: SessionRules): void;
  /** Renames this machine's own seat, for a host that rolled itself a new name. */
  setName(name: string): void;
  /** Puts a character in this machine's own seat, for a host that respecced before hosting. */
  setSheet(sheet: CharacterSheet): void;
  /** Tells everyone the session is over, on the reliable channel, before the connection goes. */
  announceEnding(): void;
  dispose(): void;
}

export function createLobbyHost(options: LobbyHostOptions): LobbyHost {
  const { transport } = options;
  let table: SeatTable = createSeatTable(transport.selfId, options.playerName, options.playerSheet);
  let rules: SessionRules = DEFAULT_SESSION_RULES;
  let stopSweep: (() => void) | null = null;
  const seatListeners = new Set<(seats: readonly Seat[]) => void>();
  const latency = createRttWindow();
  /** The last roster that went out, so a fresh one can be compared against what a player sees. */
  let published: readonly RosterSeat[] = [];

  const send = (message: LobbyMessage, target?: PeerId): void => {
    transport.send('event', MESSAGE_KINDS.lobby, encodeLobby(message), target);
  };

  /** The seating, out to everyone and up to this machine's own screen, from one reading. */
  const publish = (): void => {
    const seats = rosterOf(table, transport, latency);
    published = seats;
    options.onEvent({ kind: 'roster', seats });
    send({ kind: 'roster', seats });
    for (const listener of [...seatListeners]) listener(table.seats);
  };

  /**
   * A roster, but only if a connection reads differently than it did last time it was sent.
   *
   * A candidate sample, a channel opening and a fresh round trip all land often, and every other
   * fact about a seat publishes itself. Only a reading a player would see change on their own
   * screen is worth waking four machines to redraw.
   */
  const publishIfQualityMoved = (): void => {
    const seats = rosterOf(table, transport, latency);
    const moved = seats.some((seat) => {
      const before = published.find((row) => row.playerId === seat.playerId);
      return before === undefined || !qualityEqual(before.quality, seat.quality);
    });
    if (moved) publish();
  };

  /**
   * The heartbeat measures, this reads. Sampled at the cadence it is measured at, because a faster
   * tick would only add the same number twice to a window whose whole job is to be honest about
   * the last five seconds.
   */
  const sampleLatency = (): void => {
    for (const peer of transport.peers()) latency.sample(peer.id, transport.rttOf(peer.id));
    publishIfQualityMoved();
  };

  const stopSampling = options.repeat(HEARTBEAT_EVERY_MS, sampleLatency);

  const announceLeft = (playerId: number, name: string, reason: LeftReason): void => {
    send({ kind: 'left', playerId, reason });
    options.onNotice(leftNotice(name, reason));
  };

  const sweep = (): void => {
    const swept = expire(table, options.nowMs());
    if (swept.table === table) return;

    for (const playerId of swept.expired) {
      announceLeft(playerId, seatOfPlayer(table, playerId)?.name ?? '', 'timedOut');
    }
    table = swept.table;
    settleSweeping();
    publish();
  };

  /** A sweep runs only while there is something to sweep. */
  const settleSweeping = (): void => {
    const holding = occupancy(table).held > 0;
    if (holding && stopSweep === null) stopSweep = options.repeat(EXPIRY_TICK_MS, sweep);
    if (!holding && stopSweep !== null) {
      stopSweep();
      stopSweep = null;
    }
  };

  const listeners: TransportListeners = {
    ...silentListeners(),

    onPeerJoin: (peer): void => {
      if (peer.role !== 'guest' || seatOfPeer(table, peer.id) !== null) return;

      const seated = claim(table, peer.id, options.mintToken());
      if (seated === null) {
        // Two ways to arrive at a full table. A returning player is let past the door against the
        // chair being kept for it and lands here before the token naming that chair does: it sits
        // down a moment later, in `rejoin`. With nothing held, nobody is coming back, so this is a
        // guest that got past the cap, and seating it would put a fifth body in a four-body world.
        if (occupancy(table).held === 0) {
          options.onEvent({ kind: 'note', text: 'A player arrived with no seat left for them.' });
        }
        return;
      }

      table = seated.table;
      send({ kind: 'seat', playerId: seated.seat.playerId, token: seated.seat.token }, peer.id);
      // Sent to the newcomer alone rather than broadcast: everybody else already has it, and a
      // guest that started the game not knowing the rules would be playing a different one.
      send({ kind: 'rules', rules }, peer.id);
      options.onNotice(joinedNotice(seated.seat.name));
      publish();
    },

    onPeerUpdate: (): void => {
      publishIfQualityMoved();
    },

    onPeerLeave: (id): void => {
      const seat = seatOfPeer(table, id);
      if (seat === null) return;

      // A returning peer measures its own link from nothing. Five seconds of somebody else's
      // round trips would be a band about a connection that no longer exists.
      latency.forget(id);
      table = hold(table, id, options.nowMs() + SEAT_HOLD_MS);
      announceLeft(seat.playerId, seat.name, 'lost');
      settleSweeping();
      publish();
    },

    onMessage: (from, frame): void => {
      if (frame.kind !== MESSAGE_KINDS.lobby) return;
      receive(from, decodeLobby(frame.payload));
    },
  };

  /** The five things a guest is allowed to say. Every other tag is this machine's to send. */
  function receive(from: PeerId, message: LobbyMessage): void {
    if (message.kind === 'ready') {
      const next = setReady(table, from, message.ready);
      if (next === table) return;
      table = next;
      publish();
      return;
    }

    // A guest saying what it is called, on arriving and on every re-roll after that. Sanitized on
    // the way off the wire, so what is left is either printable and short or nothing at all, and
    // nothing at all falls back to the number rather than blanking a row in everybody's list.
    if (message.kind === 'name') {
      const seat = seatOfPeer(table, from);
      if (seat === null) return;
      const chosen = message.name === '' ? defaultSeatName(seat.playerId) : message.name;
      const next = rename(table, from, chosen);
      if (next === table) return;
      table = next;
      publish();
      return;
    }

    // A guest saying who it is playing as, on arriving and on every reconnection. Refused ids and
    // clamped magnitudes are the decoder's business, so what arrives here is already a sheet this
    // build can step a body from.
    if (message.kind === 'sheet') {
      const next = announceSheet(table, from, message.sheet);
      if (next === table) return;
      table = next;
      publish();
      return;
    }

    if (message.kind === 'leaving') {
      const seat = seatOfPeer(table, from);
      if (seat === null) return;
      table = release(table, from);
      announceLeft(seat.playerId, seat.name, 'quit');
      publish();
      return;
    }

    // A guest that has just started listening, asking for what it may have missed. Answering is
    // cheap and idempotent; a guest with no seat yet is one whose peer has not been announced here
    // either, and `onPeerJoin` will seat it in a moment.
    if (message.kind === 'arrived') {
      const seat = seatOfPeer(table, from);
      if (seat === null) return;
      send({ kind: 'seat', playerId: seat.playerId, token: seat.token }, from);
      publish();
      return;
    }

    if (message.kind === 'rejoin') {
      const back = reclaim(table, from, message.token);
      if (back === null) {
        options.onEvent({ kind: 'note', text: 'Somebody claimed a seat that is not theirs.' });
        return;
      }
      table = back.table;
      settleSweeping();
      send({ kind: 'seat', playerId: back.seat.playerId, token: back.seat.token }, from);
      send({ kind: 'rules', rules }, from);
      options.onNotice(rejoinedNotice(back.seat.name));
      publish();
    }
  }

  options.onEvent({ kind: 'seated', playerId: table.seats[0]?.playerId ?? 1 });
  publish();

  return {
    listeners,

    seats: createSeatDirectory({
      table: (): SeatTable => table,
      subscribe: (listener): (() => void) => {
        seatListeners.add(listener);
        return (): void => {
          seatListeners.delete(listener);
        };
      },
    }),

    heldTokens: (): readonly string[] =>
      table.seats.filter((seat) => seat.heldUntilMs !== null).map((seat) => seat.token),

    setName: (name: string): void => {
      const next = rename(table, transport.selfId, name);
      if (next === table) return;
      table = next;
      publish();
    },

    setSheet: (sheet: CharacterSheet): void => {
      const next = announceSheet(table, transport.selfId, sheet);
      if (next === table) return;
      table = next;
      publish();
    },

    setRules: (next: SessionRules): void => {
      if (rulesEqual(rules, next)) return;
      rules = next;
      options.onEvent({ kind: 'rules', rules });
      send({ kind: 'rules', rules });
    },

    announceEnding: (): void => {
      send({ kind: 'ending', reason: 'hostLeft' });
    },

    dispose(): void {
      stopSweep?.();
      stopSweep = null;
      stopSampling();
      seatListeners.clear();
    },
  };
}

interface PeerSignals {
  readonly snapshotChannelReady: boolean;
  readonly relayed: boolean | null;
}

/**
 * The host reports the path and the latency, not the health.
 *
 * A peer that has stopped answering is on its way to being a leave, and until the transport says so
 * the honest reading of the connection is the one it still has. A guest whose own link is wobbling
 * says so on its own screen, from its own connection status, where the fact is actually known.
 */
function signalsOf(peer: PeerSignals, rttMs: number | null): QualitySignals {
  return {
    snapshotChannelReady: peer.snapshotChannelReady,
    relayed: peer.relayed,
    unstable: false,
    rttMs,
  };
}

/** The seating as everyone else is told it, quality included, in the host's words. */
function rosterOf(
  table: SeatTable,
  transport: Transport,
  latency: RttWindow,
): readonly RosterSeat[] {
  const peers = new Map(transport.peers().map((peer) => [peer.id, peer]));

  return table.seats.map((seat) => {
    const peer = seat.peerId === null ? undefined : peers.get(seat.peerId);
    return {
      playerId: seat.playerId,
      name: seat.name,
      // A seat whose owner has not announced itself yet reads as the baseline, which is the body
      // that player would have had. The roster is what everybody draws; the null is kept in the
      // seat table, where the one thing that has to wait for a real sheet can see it.
      sheet: seat.sheet ?? DEFAULT_SEAT_SHEET,
      ready: seat.ready,
      host: seat.peerId !== null && seat.peerId === transport.selfId,
      held: seat.heldUntilMs !== null,
      quality: qualityFor(seat, peer, transport, latency),
    };
  });
}

function qualityFor(
  seat: Seat,
  peer: PeerSignals | undefined,
  transport: Transport,
  latency: RttWindow,
): LinkQuality {
  // The host is not connected to itself, so it has no round trip to itself either, and calling its
  // own link unknown reads as a fault.
  if (seat.peerId === transport.selfId) return { path: 'direct', band: 'unmeasured' };
  if (peer === undefined || seat.peerId === null) return { path: 'unknown', band: 'unmeasured' };
  return qualityOf(signalsOf(peer, latency.medianOf(seat.peerId)));
}
